import { z } from 'zod';
import { validateSingleFile, DEFAULT_MAX_FILE_SIZE_MB } from '@/utils/fileValidation';

const uploadResponseSchema = z.object({
  success: z.boolean().nullish(),
  conversation_id: z.string(),
  thread_id: z.string().nullish(),
  document_id: z.string().nullish(),
  status: z.string().nullish(),
  index_id: z.string().nullish(),
});

export type DocumentUploadResponse = z.infer<typeof uploadResponseSchema>;

const uploadFilesResponseSchema = z.object({
  success: z.boolean().nullish(),
  conversation_id: z.string(),
  thread_id: z.string().nullish(),
  session_id: z.string().nullish(),
  document_id: z.string().nullish(),
  status: z.string().nullish(),
  files: z.array(z.object({
    filename: z.string().nullish(),
    success: z.boolean().nullish(),
    document_id: z.string().nullish(),
    status: z.string().nullish(),
    error: z.string().nullish(),
    code: z.string().nullish(),
  })).nullish(),
});

export type UploadDocumentsResponse = z.infer<typeof uploadFilesResponseSchema>;

export async function uploadDocuments(
  files: File[],
  options: UploadDocumentOptions = {}
): Promise<UploadDocumentsResponse> {
  if (files.length === 0) throw new Error('No files provided');

  const maxSizeMB = options.maxSizeMB || DEFAULT_MAX_FILE_SIZE_MB;
  for (const file of files) {
    const validationResult = validateSingleFile(file, maxSizeMB, options.allowedExtensions);
    if (!validationResult.valid) {
      throw new Error(`${file.name}: ${validationResult.errors.map(e => e.reason).join('; ')}`);
    }
  }

  if (options.isSnowbotAttachmentFlow) {
    return {
      success: true,
      conversation_id: options.conversationId ?? '',
      thread_id: options.sessionId,
      files: files.map(f => ({ filename: f.name, success: true, status: 'skipped' })),
    };
  }

  const formData = new FormData();
  for (const file of files) {
    formData.append('file', file); // multiple 'file=' fields → List[UploadFile] on backend
  }
  if (options.conversationId) formData.append('conversation_id', options.conversationId);
  if (options.sessionId) formData.append('session_id', options.sessionId);
  if (options.metadata) formData.append('metadata', JSON.stringify(options.metadata));

  const response = await fetch('/document/upload', {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });

  const contentType = response.headers.get('content-type');
  if (!contentType?.includes('application/json')) {
    throw new Error('Unexpected response from upload service');
  }

  const payload = await response.json();

  if (!response.ok) {
    const message = typeof payload?.error === 'string' ? payload.error : 'Document upload failed';
    throw new Error(message);
  }

  return uploadFilesResponseSchema.parse(payload);
}

export interface UploadDocumentOptions {
  isSnowbotAttachmentFlow?: boolean;
  conversationId?: string;
  sessionId?: string;
  metadata?: Record<string, unknown>;
  maxSizeMB?: number;
  allowedExtensions?: string[];
}

export async function uploadDocument(
  file: File,
  options: UploadDocumentOptions = {}
): Promise<DocumentUploadResponse> {
  const maxSizeMB = options.maxSizeMB || DEFAULT_MAX_FILE_SIZE_MB;
  const allowedExtensions = options.allowedExtensions;

  console.log('[uploadDocument] ⚠️ DOCUMENT UPLOAD CALLED', {
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
    conversationId: options.conversationId,
    sessionId: options.sessionId,
    hasMetadata: !!options.metadata,
    isSnowbotAttachmentFlow: options.isSnowbotAttachmentFlow,
    maxSizeMB,
    hasAllowedExtensions: !!allowedExtensions
  });

  // Validate file before upload
  const validationResult = validateSingleFile(file, maxSizeMB, allowedExtensions);
  if (!validationResult.valid) {
    console.error('[uploadDocument] File validation failed', {
      fileName: file.name,
      errors: validationResult.errors
    });
    const errorMessage = validationResult.errors.map(e => e.reason).join('; ');
    throw new Error(errorMessage);
  }

  console.log('[uploadDocument] File validation passed', {
    fileName: file.name,
    fileSize: file.size
  });

  const formData = new FormData();
  formData.append('file', file);
  
  if (options.conversationId) {
    console.log('[uploadDocument] Adding conversationId to form', {
      conversationId: options.conversationId
    });
    formData.append('conversation_id', options.conversationId);
  }

  if (options.sessionId) {
    console.log('[uploadDocument] Adding sessionId to form', {
      sessionId: options.sessionId
    });
    formData.append('session_id', options.sessionId);
  }

  if (options.metadata) {
    console.log('[uploadDocument] Adding metadata to form', {
      metadata: options.metadata
    });
    formData.append('metadata', JSON.stringify(options.metadata));
  }

  console.log('[uploadDocument] Sending POST request to /document/upload');

  const isSnowbotAttachmentFlow = options.isSnowbotAttachmentFlow ?? false;

  console.log('[uploadDocument] ⚠️ FLOW DECISION', {
    isSnowbotAttachmentFlow,
    willSkipEndpoint: isSnowbotAttachmentFlow,
    willCallEndpoint: !isSnowbotAttachmentFlow
  });

  if (isSnowbotAttachmentFlow) { 
    console.warn('[uploadDocument] ✅ SNOW ATTACHMENT FLOW - Skipping /document/upload endpoint', {
      fileName: file.name,
      conversationId: options.conversationId,
      sessionId: options.sessionId
    });

    const fallbackResponse: DocumentUploadResponse = {
      success: true,
      conversation_id: options.conversationId ?? '',
      thread_id: options.sessionId,
      document_id: undefined,
      status: 'skipped',
      index_id: undefined
    };

    return fallbackResponse;
  }

  // Non-Snowbot flow, call AVA /document/upload endpoint
  console.log('[uploadDocument] ❌ CALLING /document/upload ENDPOINT (RAG flow)', {
    fileName: file.name
  });

  const response = await fetch('/document/upload', {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });

  console.log('[uploadDocument] Response received', {
    status: response.status,
    statusText: response.statusText,
    headers: {
      contentType: response.headers.get('content-type'),
      contentLength: response.headers.get('content-length')
    }
  });

  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    console.error('[uploadDocument] Invalid content type', { contentType });
    throw new Error('Unexpected response from upload service');
  }

  const payload = await response.json();

  console.log('[uploadDocument] Response payload parsed', {
    success: response.ok,
    payloadKeys: Object.keys(payload || {}),
    payload
  });

  if (!response.ok) {
    const message = typeof payload?.error === 'string' ? payload.error : 'Document upload failed';
    console.error('[uploadDocument] Upload failed', {
      message,
      status: response.status,
      payload
    });
    throw new Error(message);
  }

  console.log('[uploadDocument] Validating response schema');
  const validatedResponse = uploadResponseSchema.parse(payload);
  
  console.log('[uploadDocument] Upload successful', {
    conversationId: validatedResponse.conversation_id,
    threadId: validatedResponse.thread_id,
    documentId: validatedResponse.document_id,
    status: validatedResponse.status
  });

  return validatedResponse;
}
